<script lang="ts" setup>
import { useRoute, useRouter } from 'vue-router';
import { AppTypography } from '~/components/base';
import type { BreadcrumbItem } from '~/types/components/base/Breadcrumb';
import ROUTE_LABELS from '~/utils/enums/RouteLabels';

const { customBreadcrumbs = [], includeHome = true } = defineProps<{
  customBreadcrumbs?: BreadcrumbItem[];
  includeHome?: boolean;
}>();

const route = useRoute();
const router = useRouter();

const redirect = computed(() => route.query.redirect as string | undefined);

const routes = computed((): BreadcrumbItem[] => {
  if (customBreadcrumbs.length > 0) {
    return customBreadcrumbs.map((breadcrumb, index, arr) => ({
      label: breadcrumb.label,
      to: index < arr.length - 1 ? breadcrumb.to : undefined,
    }));
  }

  const pathSegments = route.path.split('/').filter(Boolean);
  const breadcrumbs = [];

  if (route.path !== '/' && includeHome) {
    breadcrumbs.push({
      label: 'Home',
      to: '/',
    });
  }

  let currentPath = '';
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const routeConfig = ROUTE_LABELS[currentPath];
    const label = routeConfig?.label || segment;
    const isHidden = routeConfig?.isHidden || false;
    const isLast = index === pathSegments.length - 1;

    if (!isHidden) {
      breadcrumbs.push({
        label,
        to: isLast
          ? undefined
          : index === pathSegments.length - 2 && redirect.value
            ? redirect.value
            : currentPath,
      });
    }
  });

  return breadcrumbs;
});

const goBack = () => {
  router.back();
};

defineExpose({
  routes,
});
</script>

<template>
  <div class="app-breadcrumbs flex w-full items-center gap-2 p-2">
    <Icon
      name="solar:arrow-left-linear"
      size="24"
      class="cursor-pointer"
      @click="goBack()"
    />
    <div class="flex items-center justify-center gap-2">
      <template v-for="(breadcrumb, index) in routes" :key="index">
        <NuxtLink
          v-if="breadcrumb.to"
          :to="breadcrumb.to"
          class="font-subheadline-bold"
        >
          {{ breadcrumb.label }}
        </NuxtLink>
        <AppTypography
          v-else
          type="subheadline-bold"
          class="text-color-tertiary"
        >
          {{ breadcrumb.label }}
        </AppTypography>
        <Icon
          v-if="index < routes.length - 1"
          name="solar:alt-arrow-right-linear"
          size="24"
        />
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.app-breadcrumbs {
}
</style>
